import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from '@/components/ui/Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

// Toda variante declara a própria cor de borda (mesmo que transparente) para
// que o estado desabilitado só troque a cor, nunca a caixa do botão.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-[var(--accent)] text-[var(--accent-contrast)] shadow-sm hover:brightness-110 active:brightness-95',
  secondary:
    'border-[var(--border-strong)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:bg-[var(--surface-raised)] active:bg-[var(--surface-inset)]',
  ghost:
    'border-transparent bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)] active:bg-[var(--surface-raised)]',
  // `--accent-contrast` acompanha o tema, garantindo contraste sobre o preenchimento saturado.
  danger:
    'border-transparent bg-[var(--danger)] text-[var(--accent-contrast)] shadow-sm hover:brightness-110 active:brightness-95',
  subtle:
    'border-transparent bg-[var(--surface-inset)] text-[var(--text-primary)] hover:bg-[var(--surface-raised)] active:bg-[var(--surface-inset)]',
}

/**
 * Indisponível é um estado, não uma transparência: opacidade sozinha deixava
 * o primário desabilitado com cara de CTA no tema escuro. Aqui o preenchimento
 * inteiro troca por tokens neutros, igual em todas as variantes.
 */
const DISABLED_CLASS =
  'bg-[var(--surface-inset)] text-[var(--text-muted)] border-[var(--border-subtle)] shadow-none cursor-not-allowed'

/*
 * Alvos de toque: 44px em telas pequenas, onde o dedo é o único apontador.
 * A partir de `sm` o botão volta aos 40px do desktop — é a altura dos `Input`
 * (`h-10`), e `sm`/`icon` convivem na mesma barra de ferramentas do calendário,
 * então mexer só em um deles desalinharia a fileira.
 */
const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'h-11 gap-1.5 px-3 text-[0.8125rem] sm:h-10',
  md: 'h-11 gap-2 px-4 text-sm sm:h-10',
  lg: 'h-11 gap-2.5 px-5 text-base',
  icon: 'size-11 justify-center sm:size-10',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Mostra spinner, marca `aria-busy` e bloqueia novos cliques. */
  loading?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    disabled,
    type = 'button',
    className,
    children,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        // A borda de 1px existe em todos os estados (transparente quando a
        // variante não a usa) para o desabilitado não mudar o tamanho do botão.
        'inline-flex select-none items-center rounded-lg border font-medium leading-none',
        'transition-[background-color,color,border-color,filter,box-shadow] duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
        isDisabled ? DISABLED_CLASS : VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        fullWidth && 'w-full justify-center',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} />
      ) : (
        leadingIcon && <span className="inline-flex shrink-0">{leadingIcon}</span>
      )}
      {/* No tamanho `icon` o spinner substitui o próprio ícone. */}
      {size === 'icon' && loading ? null : children}
      {!loading && trailingIcon && <span className="inline-flex shrink-0">{trailingIcon}</span>}
    </button>
  )
})
